import {
  ChevronsUpDown,
  LogOut,
  Monitor,
  Moon,
  Settings,
  Sun,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSession } from '@/hooks/use-session';
import { useTheme } from '@/hooks/use-theme';
import { cn } from '@/lib/utils';

const initialsFrom = (name: string | null, email: string): string => {
  const source = name?.trim() || email;

  return source
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');
};

export const UserMenu = ({
  sidebar = false,
  collapsed = false,
  mobile = false,
}: {
  sidebar?: boolean;
  collapsed?: boolean;
  mobile?: boolean;
}) => {
  const { user, signOut } = useSession();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  if (!user) {
    return null;
  }

  const handleSignOut = async () => {
    await signOut();
    navigate('/signin', { replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'flex items-center text-left transition-colors',
          sidebar
            ? 'h-10 w-full rounded-lg hover:bg-surface-muted'
            : 'size-8 justify-center rounded-full hover:opacity-85',
          sidebar && !collapsed ? 'gap-2 px-2' : 'justify-center',
        )}
        aria-label="Account menu"
        title={sidebar && collapsed ? user.name || user.email : undefined}
      >
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent-subtle text-xs font-semibold text-accent">
          {initialsFrom(user.name, user.email)}
        </span>

        {sidebar && !collapsed ? (
          <>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-ink">
                {user.name || user.email}
              </span>
              {user.name ? (
                <span className="block truncate text-xs text-ink-subtle">
                  {user.email}
                </span>
              ) : null}
            </span>
            <ChevronsUpDown className="size-4 shrink-0 text-ink-subtle" />
          </>
        ) : null}
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        side={sidebar ? (mobile ? 'top' : 'right') : 'bottom'}
      >
        <DropdownMenuLabel>
          <span className="block truncate text-sm font-medium text-ink">
            {user.name || user.email}
          </span>
          {user.name ? (
            <span className="block truncate font-normal">{user.email}</span>
          ) : null}
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {!sidebar ? (
          <>
            <DropdownMenuItem onSelect={() => navigate('/account')}>
              <Settings />
              Account settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : null}

        <DropdownMenuLabel>Theme</DropdownMenuLabel>

        {(
          [
            { value: 'light', label: 'Light', icon: Sun },
            { value: 'dark', label: 'Dark', icon: Moon },
            { value: 'system', label: 'System', icon: Monitor },
          ] as const
        ).map(({ value, label, icon: Icon }) => (
          <DropdownMenuItem
            key={value}
            // Radix closes the menu on select; keeping it open lets the user see
            // the theme change land before dismissing it themselves.
            onSelect={(event) => {
              event.preventDefault();
              setTheme(value);
            }}
          >
            <Icon className={theme === value ? 'text-accent' : undefined} />
            {label}
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />

        <DropdownMenuItem destructive onSelect={handleSignOut}>
          <LogOut />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
